// New: CompanySearch component - MUI Autocomplete with debounced previews.
// Expects getCompaniesByName(query) in ../controller.tsx and uses react-router's navigate to go to /company/:id
import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, Avatar, ListItem, ListItemAvatar, ListItemText, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getCompaniesByName } from '../controller.tsx';

const CompanySearch = ({ placeholder = 'Search companies...' }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]); // { id, name, logo, address, description }
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const mountedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    // Only search when there are at least 2 characters to reduce noise
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    // debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        if (typeof getCompaniesByName === 'function') {
          const res = await getCompaniesByName(inputValue);
          if (!mountedRef.current) return;
          if (Array.isArray(res)) {
            // Normalize to known fields
            const normalized = res.map((c) => ({
              id: c?.id ?? null,
              name: c?.name ?? 'Unknown',
              logo: c?.logo ?? null,
              address: c?.address ?? '',
              description: c?.description ?? '',
            }));
            setOptions(normalized);
          } else {
            setOptions([]);
          }
        } else {
          // controller function missing: just clear and log
          // eslint-disable-next-line no-console
          console.warn('getCompaniesByName is not implemented in controller.tsx');
          setOptions([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Company search failed', err);
        if (mountedRef.current) setOptions([]);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      freeSolo={false}
      filterOptions={(x) => x} // already filtered by backend
      options={options}
      getOptionLabel={(option) => (option && option.name) || ''}
      noOptionsText={inputValue && inputValue.length >= 2 ? 'No results' : 'Type at least 2 characters'}
      loading={loading}
      onChange={(e, value) => {
        if (value && value.id != null) {
          navigate(`/company/${value.id}`);
        }
      }}
      inputValue={inputValue}
      onInputChange={(e, newInput) => setInputValue(newInput)}
      sx={{ width: { xs: '100%', sm: 360 }, maxWidth: 520 }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id} sx={{ gap: 1 }}>
          <ListItemAvatar>
            {option.logo ? (
              <Avatar src={option.logo} alt={option.name} />
            ) : (
              <Avatar>{(option.name || 'C').charAt(0).toUpperCase()}</Avatar>
            )}
          </ListItemAvatar>
          <ListItemText
            primary={option.name}
            secondary={option.address || option.description}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
          />
        </ListItem>
      )}
    />
  );
};

export default CompanySearch;